/**
 * Appels intervals.icu (`ARCHITECTURE_V2.md` §5.1 à §5.3 ; specs §7.3, §12.4).
 *
 * 🔴 **Rien n'est jamais poussé VERS intervals.icu ni vers Coros.** La
 * synchronisation est entrante à toutes les versions (specs §6.6, §12.4), et
 * c'est la seule interdiction absolue de ce module. Les deux écritures qu'il
 * porte s'adressent à **notre** serveur : l'une lui demande d'aller lire, l'autre
 * lui donne de quoi le faire.
 *
 * 🔴 **Deux revirements assumés du 05/09/2026, tous deux demandés après usage.**
 * La première version n'avait qu'un `fetch` : ni bouton de synchronisation, ni
 * champ de clé. Les deux refus étaient argumentés, et tous deux prenaient le
 * problème par le mauvais bout :
 *
 *   - « une route de déclenchement serait une **action**, la famille de
 *     `POST /api/notifications/test` que le §2.4 range hors file ». C'est exact,
 *     et cela ne conclut rien : `POST /api/notifications/test` **existe** comme
 *     route. Être une action veut dire *l'exclure de la file*, pas *ne pas la
 *     construire* ;
 *   - « la clé reste un secret serveur, aucun champ de saisie ». Défendable tant
 *     que le geste est rare — mais le poser demandait un `scp`, un `ssh -t`, un
 *     mot de passe sudo et un script. Acceptable deux fois dans la vie de
 *     l'application ; plus du tout à la première clé refusée.
 *
 * ⚠️ **La clé ne revient JAMAIS du serveur**, pas même tronquée. Ce que ce
 * module lit, c'est `configured` et `keySource` — assez pour dire ce qui est en
 * vigueur, jamais assez pour reconstituer un secret.
 */

import type { IntervalsKeyInput, IntervalsResponse } from '../../../shared/types';
import { apiFetch } from './client';

/**
 * Clé de cache — **une seule pour toute l'application**.
 *
 * ⚠️ Progression et Réglages la partagent délibérément : le second n'a besoin
 * que de `status`, et lui donner sa propre route aurait coûté un second
 * aller-retour pour deux lignes de texte, plus une seconde entrée à persister.
 * C'est la discipline déjà appliquée au rappel de pesée du journal, qui
 * interroge `weightKey('90d')` — la plage par défaut de Progression — « pour que
 * les deux écrans partagent la même entrée de cache ».
 */
export const INTERVALS_KEY = ['intervals'] as const;

/** Statut, fenêtre de 14 jours et journées, en un appel. */
export function fetchIntervals(): Promise<IntervalsResponse> {
  return apiFetch<IntervalsResponse>('/api/intervals');
}

/**
 * Force une ingestion, tout de suite.
 *
 * 🔴 **C'est une ACTION, pas une écriture de données** — la famille de
 * `POST /api/notifications/test`, qui existe elle aussi comme route tout en
 * étant **hors file** (§2.4). Rejouée le lendemain elle n'aurait plus d'objet :
 * le tick de fond s'en sera chargé quarante-huit fois.
 *
 * ⚠️ **Hors ligne, elle échoue franchement**, et c'est le comportement juste :
 * il n'y a rien à importer d'un tiers sans réseau. L'écran affiche le motif
 * plutôt que de faire semblant d'avoir mis en attente.
 *
 * ⚠️ Renvoie l'**état complet** — statut, fenêtre rafraîchie, et le verdict de
 * cette ingestion-ci dans `lastRun`. Un 204 imposerait un second aller-retour.
 */
export function syncIntervals(): Promise<IntervalsResponse> {
  return apiFetch<IntervalsResponse>('/api/intervals/sync', { method: 'POST' });
}

/**
 * Pose la clé d'API, ou la retire (`null`).
 *
 * 🔴 **La clé part en clair dans le corps de la requête** — sur HTTPS, vers
 * notre propre serveur, et c'est le seul chemin possible : elle doit y arriver
 * pour être réémise vers intervals.icu. Elle est ensuite stockée en base, donc
 * présente **en clair dans les sauvegardes** (§8.2). À savoir, pas à découvrir.
 *
 * ⚠️ **Hors file** (`shared/syncable.ts`), et le motif n'est pas le même que
 * pour la synchronisation : celle-ci serait techniquement rejouable sans
 * dommage. Ce qui l'exclut, c'est qu'une opération en attente **dort en clair
 * dans IndexedDB** jusqu'au retour du réseau. Un secret ne se met pas en file.
 *
 * ⚠️ Le serveur déclenche une ingestion dans la foulée : le verdict de
 * `lastRun` répond à « ma clé est-elle bonne ? », ce qu'aucune validation de
 * forme ne saurait dire.
 */
export function saveIntervalsKey(apiKey: string | null): Promise<IntervalsResponse> {
  return apiFetch<IntervalsResponse>('/api/intervals/key', {
    method: 'PUT',
    body: JSON.stringify({ apiKey } satisfies IntervalsKeyInput),
  });
}
